import { type FC, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useMeasure from 'react-use-measure';
import { type GoodsType } from '@common/types/goods';
import { storageTextObj } from '@components/helpers/ObjectsText';
import { H2Motion } from '@components/Typography/Typography';
import cn from 'classnames';
import { useAnimate } from 'framer-motion';

import { useClearWorkshopProductionMutation } from '@/api/queries/workshop';
import { convertRemToPixels } from '@/common/lib/convertRemtoPx';
import { ModalContext } from '@/components/Modals/Modal/config';
import AmountToProduce from '@/components/Plant/AmountToProduce/AmountToProduce';
import { WorkshopContext } from '@/pages/Workshop/context';

import { ProductionRecipe } from '../ProductionRecipe/PruductionRecipe';

import { GoodsSwiper } from './GoodsSwiper/GoodsSwiper';
import { GoodsDetailContext } from './context';

import css from './styles.module.scss';

interface WorkshopGoodsProps {
  activeGoodsName?: GoodsType;
  toBeProduced: number;
  workshopId: string;
  clearDisabled: boolean;
}

const WorkshopGoods: FC<WorkshopGoodsProps> = ({
  activeGoodsName,
  toBeProduced,
  workshopId,
  clearDisabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { toAdd } = useContext(WorkshopContext);
  const [ref, titleRect] = useMeasure();
  const { t } = useTranslation();
  const [scope, animate] = useAnimate();
  const [clearWorkshopProduction, clearWorkshopProductionApiState] =
    useClearWorkshopProductionMutation();
  const { openModal } = useContext(ModalContext);

  const animateTitle = useCallback(
    (wrapperYPosition?: number) => {
      let yPosition = 0;

      if (wrapperYPosition) {
        yPosition =
          wrapperYPosition -
          convertRemToPixels(23) * 0.3 +
          convertRemToPixels(1.5) -
          titleRect.y;
      }

      animate(
        scope.current,
        { y: yPosition },
        { duration: 0.5, ease: 'easeOut' },
      );
    },
    [animate, scope, titleRect.y, activeGoodsName],
  );

  const openBoostProduction = (): void => {
    if (activeGoodsName && toBeProduced > 0) {
      openModal({
        renderKind: 'boostProduction',
        props: { plantId: workshopId, material: activeGoodsName },
      });
    }
  };

  const clearProduction = (): void => {
    clearWorkshopProduction({ id: workshopId });
  };

  const texts = storageTextObj(t);

  return (
    <GoodsDetailContext.Provider value={{ isOpen, setIsOpen, animateTitle }}>
      <div className="w-full flex-1 flex flex-col justify-center items-center mt-[1.125rem]">
        <div ref={ref} className="relative">
          <H2Motion
            ref={scope}
            weight="black"
            className={cn(css.name, 'relative z-[2]')}
          >
            {activeGoodsName && activeGoodsName in texts
              ? texts[activeGoodsName as string]
              : ' '}
          </H2Motion>
        </div>

        <div className="w-full h-36 relative mb-2">
          <div className="absolute top-0 left-0 w-full h-full">
            <GoodsSwiper workshopId={workshopId} />
          </div>
        </div>
        <div className="mt-2 mb-[5.61vh]">
          <AmountToProduce
            onBoost={openBoostProduction}
            cancelDisabled={
              clearWorkshopProductionApiState.isLoading || clearDisabled
            }
            onCancel={clearProduction}
            value={toBeProduced}
            key={'amount-' + activeGoodsName}
          />
        </div>

        <ProductionRecipe
          toAdd={toAdd}
          workshopId={workshopId}
          goodsName={activeGoodsName}
          withStock={true}
        />
      </div>
    </GoodsDetailContext.Provider>
  );
};

export { WorkshopGoods };
